import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { InstructionGuide } from '../components/layout/InstructionGuide';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { TrendingUp, TrendingDown, Users, Store, BarChart3, FileDown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';

const COLORS = ['#D97706', '#F59E0B', '#FBBF24', '#1E293B', '#64748B'];

export default function Performance() {
  const { api, userZone, selectedBusiness } = useApp();
  const [analytics, setAnalytics] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = userZone ? { zone: userZone } : {};
      if (selectedBusiness) params.business_id = selectedBusiness;
      const res = await api.getPerformanceAnalytics(params);
      setAnalytics(res.data);
    } catch (e) { console.error(e); }
  }, [api, userZone, selectedBusiness]);

  useEffect(() => { load(); }, [load]);

  if (!analytics) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-slate-400">Loading analytics...</div></div>;

  return (
    <div data-testid="performance-page" className="space-y-6 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Performance Analytics</h1>
          <p className="text-sm text-slate-500 mt-1">{userZone ? `${userZone} zone` : 'All zones'} - Business performance and growth insights</p>
        </div>
        <a href={api.exportPerformance(userZone)} download data-testid="export-performance-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
          <FileDown className="w-4 h-4" /> Export Report
        </a>
      </div>

      {/* Revenue by Zone */}
      <Card className="border border-border/60 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-amber-600" /> Revenue vs Target by Zone
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics.zones} barSize={28}>
                <XAxis dataKey="zone" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748B' }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94A3B8' }} tickFormatter={v => `${(v/100000).toFixed(0)}L`} />
                <Tooltip formatter={(v) => `${(v/1000).toFixed(0)}K`} />
                <Legend />
                <Bar dataKey="revenue" fill="#D97706" radius={[4, 4, 0, 0]} name="Revenue" />
                <Bar dataKey="target" fill="#FDE68A" radius={[4, 4, 0, 0]} name="Target" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Visits by User */}
        <Card className="lg:col-span-6 border border-border/60 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Users className="w-4 h-4 text-amber-600" /> Visits by Team Member
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics.visit_by_user} layout="vertical" barSize={16}>
                  <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94A3B8' }} />
                  <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748B' }} width={100} />
                  <Tooltip />
                  <Bar dataKey="total_visits" fill="#D97706" radius={[0, 4, 4, 0]} name="Total" />
                  <Bar dataKey="completed_visits" fill="#10B981" radius={[0, 4, 4, 0]} name="Completed" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Growth Tracking */}
        <Card className="lg:col-span-6 border border-border/60 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-amber-600" /> Growth from Existing Clients
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.growth_data?.map((g, i) => {
                const growth = g.growth?.toFixed(1);
                return (
                  <div key={i} data-testid={`growth-item-${i}`} className="flex items-center justify-between p-3 rounded-lg border border-border/40 hover:bg-slate-50/50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${g.sp_type === 'business' ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'}`}>
                        <Store className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{g.name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge className={`text-[10px] ${g.sp_type === 'business' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>{g.sp_type}</Badge>
                          <span className="text-xs text-slate-400">{(g.previous_revenue/1000).toFixed(0)}K → {(g.current_revenue/1000).toFixed(0)}K</span>
                        </div>
                      </div>
                    </div>
                    <span className={`flex items-center gap-1 text-sm font-bold ${Number(growth) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      {Number(growth) >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                      {growth}%
                    </span>
                  </div>
                );
              })}
              {(!analytics.growth_data || analytics.growth_data.length === 0) && (
                <p className="text-sm text-slate-400 text-center py-4">No growth data available</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Sales Points */}
      <Card className="border border-border/60 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
            <Store className="w-4 h-4 text-amber-600" /> Top Sales Points by Revenue
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics.top_sales_points?.slice(0, 8)} barSize={32}>
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748B' }} angle={-20} textAnchor="end" height={60} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94A3B8' }} tickFormatter={v => `${(v/1000).toFixed(0)}K`} />
                <Tooltip formatter={(v) => `${(v/1000).toFixed(0)}K`} />
                <Bar dataKey="current_revenue" fill="#D97706" radius={[6, 6, 0, 0]} name="Revenue" />
                <Bar dataKey="monthly_target" fill="#FDE68A" radius={[6, 6, 0, 0]} name="Target" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
